<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div
      v-if="alert.show"
      :class="[
        'mb-6 p-4 rounded-md border',
        alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
        alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
        alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
      ]"
    >
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إعدادات Agora لكل فرع</h1>
        <p class="mt-2 text-sm text-gray-700">
          مفاتيح Agora والـ S3 الخاصة بتسجيل جلسات إقرا السحابي — لكل فرع إعداداته.
          الأسرار تُحفظ كاملة لكن لا يُعرض منها إلا آخر 4 أحرف للتحقق.
        </p>
      </div>
    </div>

    <!-- Search + filter -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الفروع..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterStatus"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option value="all">كل الفروع</option>
          <option value="configured">المهيأة بالكامل</option>
          <option value="partial">مهيأة جزئياً</option>
          <option value="missing">غير مُهيأة</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الفروع: {{ tenants.length }}
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flex flex-col">
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredRows.length === 0" class="text-center py-12">
        <p class="text-gray-600">لا توجد فروع مطابقة.</p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">App ID</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Customer Secret (آخر 4)</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">إجراءات</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(row, index) in filteredRows" :key="row.tenant.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                    {{ row.tenant.name }}
                    <div class="text-xs text-gray-500">{{ row.tenant.code || row.tenant.id }}</div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      v-if="row.status === 'configured'"
                      class="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      مُهيأة
                    </span>
                    <span
                      v-else-if="row.status === 'partial'"
                      class="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                    >
                      مهيأة جزئياً
                    </span>
                    <span
                      v-else
                      class="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                    >
                      غير مُهيأة
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                    {{ row.config?.AGORA_APP_ID?.value || '—' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                    {{ formatLast4(row.config?.AGORA_CUSTOMER_SECRET) }}
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openModal(row)">
                      {{ row.status === 'missing' ? 'إعداد' : 'تعديل' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 720px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">
          إعدادات Agora — {{ activeTenant?.name }}
        </h3>
        <p class="text-sm text-gray-600 mb-4">
          اترك أي حقل فارغًا للإبقاء على القيمة الحالية.
          أدخل قيمة فارغة (مسح ما داخل الحقل ثم الحفظ) لإزالة الإعداد والرجوع للقيمة الافتراضية في الـ env.
        </p>

        <form @submit.prevent="handleSave">
          <!-- Agora section -->
          <div class="mb-6">
            <h4 class="text-sm font-semibold text-gray-800 mb-3 border-b pb-1">Agora</h4>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AGORA_APP_ID — معرّف المشروع
                </label>
                <input
                  v-model="formData.AGORA_APP_ID"
                  type="text"
                  :placeholder="placeholderFor('AGORA_APP_ID')"
                  autocomplete="off"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AGORA_CUSTOMER_KEY — Customer ID
                </label>
                <input
                  v-model="formData.AGORA_CUSTOMER_KEY"
                  type="text"
                  :placeholder="placeholderFor('AGORA_CUSTOMER_KEY')"
                  autocomplete="off"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AGORA_CUSTOMER_SECRET — Customer Secret
                </label>
                <input
                  v-model="formData.AGORA_CUSTOMER_SECRET"
                  type="password"
                  :placeholder="placeholderFor('AGORA_CUSTOMER_SECRET')"
                  autocomplete="new-password"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AGORA_STORAGE_REGION — منطقة التخزين (0 = US East، 22 = Middle East)
                </label>
                <input
                  v-model="formData.AGORA_STORAGE_REGION"
                  type="number"
                  min="0"
                  :placeholder="placeholderFor('AGORA_STORAGE_REGION')"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          <!-- S3 storage section -->
          <div>
            <h4 class="text-sm font-semibold text-gray-800 mb-3 border-b pb-1">AWS S3 (تخزين التسجيلات)</h4>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AWS_BUCKET_NAME — اسم الـ Bucket
                </label>
                <input
                  v-model="formData.AWS_BUCKET_NAME"
                  type="text"
                  :placeholder="placeholderFor('AWS_BUCKET_NAME')"
                  autocomplete="off"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AWS_ACCESS_KEY — Access Key ID
                </label>
                <input
                  v-model="formData.AWS_ACCESS_KEY"
                  type="text"
                  :placeholder="placeholderFor('AWS_ACCESS_KEY')"
                  autocomplete="off"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  AWS_SECRET_KEY — Secret Access Key
                </label>
                <input
                  v-model="formData.AWS_SECRET_KEY"
                  type="password"
                  :placeholder="placeholderFor('AWS_SECRET_KEY')"
                  autocomplete="new-password"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                />
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button
              type="button"
              @click="closeModal"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
              :disabled="saving"
            >
              إلغاء
            </button>
            <button
              type="submit"
              class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColor/90 sm:text-sm"
              :disabled="saving"
            >
              {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import url from '@/url.js';
import {
  getTenantAgoraConfig,
  upsertTenantAgoraConfig,
} from '@/services/agoraConfigs.js';

// Fields the operator can edit. Order matches the table/modal layout.
const FIELDS = [
  'AGORA_APP_ID',
  'AGORA_CUSTOMER_KEY',
  'AGORA_CUSTOMER_SECRET',
  'AGORA_STORAGE_REGION',
  'AWS_BUCKET_NAME',
  'AWS_ACCESS_KEY',
  'AWS_SECRET_KEY',
];

// "configured" requires both Agora identity AND recording creds present.
const REQUIRED_FOR_CONFIGURED = [
  'AGORA_APP_ID',
  'AGORA_CUSTOMER_KEY',
  'AGORA_CUSTOMER_SECRET',
];

const emptyForm = () => FIELDS.reduce((acc, code) => ({ ...acc, [code]: '' }), {});

export default defineComponent({
  name: 'TenantAgoraConfigsManagement',
  data() {
    return {
      tenants: [],
      configByTenant: {},
      loading: false,
      saving: false,
      searchTerm: '',
      filterStatus: 'all',
      showModal: false,
      activeTenant: null,
      formData: emptyForm(),
      alert: { show: false, type: 'success', message: '' },
    };
  },
  computed: {
    rows() {
      return this.tenants.map((tenant) => {
        const config = this.configByTenant[tenant.id] || null;
        return {
          tenant,
          config,
          status: this.computeStatus(config),
        };
      });
    },
    filteredRows() {
      let rows = this.rows;
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        rows = rows.filter((row) =>
          row.tenant.name?.toLowerCase().includes(term) ||
          row.tenant.code?.toLowerCase().includes(term),
        );
      }
      if (this.filterStatus !== 'all') {
        rows = rows.filter((row) => row.status === this.filterStatus);
      }
      return rows;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await this.loadTenants();
        await this.loadConfigsForTenants();
      } catch (err) {
        console.error('Failed to load Agora configs page', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في تحميل البيانات');
      } finally {
        this.loading = false;
      }
    },
    async loadTenants() {
      const response = await axios.get(
        process.env.VUE_APP_SERVICE_URL + url.tenants_list,
        {
          params: { page: 1, size: 1000 },
          headers: { 'X-Authorization': localStorage.getItem('token') },
        },
      );
      const result = response.data?.result?.data
        ?? response.data?.data
        ?? response.data?.result
        ?? response.data;

      let list = [];
      if (result?.pagination) {
        list = Array.isArray(result.data) ? result.data : [];
      } else if (result?.content !== undefined) {
        list = Array.isArray(result.content) ? result.content : [];
      } else if (Array.isArray(result)) {
        list = result;
      } else if (Array.isArray(result?.data)) {
        list = result.data;
      }

      this.tenants = list;
    },
    async loadConfigsForTenants() {
      // No bulk endpoint — fan out per tenant. Counts here are small (super-admin scope).
      const map = {};
      const tasks = this.tenants.map(async (tenant) => {
        try {
          map[tenant.id] = await getTenantAgoraConfig(tenant.id);
        } catch (err) {
          console.warn(`Failed to load Agora config for tenant ${tenant.id}`, err);
        }
      });
      await Promise.all(tasks);
      this.configByTenant = map;
    },
    computeStatus(config) {
      if (!config) return 'missing';
      const setCount = REQUIRED_FOR_CONFIGURED.filter((code) => config[code]?.set).length;
      if (setCount === 0) return 'missing';
      if (setCount === REQUIRED_FOR_CONFIGURED.length) return 'configured';
      return 'partial';
    },
    openModal(row) {
      this.activeTenant = row.tenant;
      this.formData = emptyForm();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.activeTenant = null;
      this.formData = emptyForm();
    },
    async handleSave() {
      if (!this.activeTenant) return;
      this.saving = true;

      // Only send fields the operator actually typed into. Blank field = "leave as-is".
      // To CLEAR a value, the operator must explicitly delete and re-save (the backend
      // treats an empty string as "delete this config row").
      // We can't distinguish "didn't touch" from "wants empty" with a single input, so
      // we treat any non-empty trimmed value as an update — blank means "skip".
      const payload = {};
      FIELDS.forEach((code) => {
        const raw = this.formData[code];
        if (raw === null || raw === undefined) return;
        const value = String(raw).trim();
        if (value !== '') {
          payload[code] = code === 'AGORA_STORAGE_REGION' ? Number(value) : value;
        }
      });

      if (Object.keys(payload).length === 0) {
        this.saving = false;
        this.showAlert('warning', 'لم تُغيَّر أي قيمة');
        return;
      }

      try {
        const fresh = await upsertTenantAgoraConfig(this.activeTenant.id, payload);
        this.configByTenant = {
          ...this.configByTenant,
          [this.activeTenant.id]: fresh,
        };
        this.showAlert('success', 'تم حفظ إعدادات Agora');
        this.closeModal();
      } catch (err) {
        console.error('Failed to save Agora config', err);
        const msg = err.response?.data?.result?.mesAr
          || err.response?.data?.mesAr
          || 'فشل في حفظ إعدادات Agora';
        this.showAlert('danger', msg);
      } finally {
        this.saving = false;
      }
    },
    placeholderFor(code) {
      const config = this.activeTenant ? this.configByTenant[this.activeTenant.id] : null;
      const meta = config?.[code];
      if (meta?.set) {
        if (meta.value) {
          return `محفوظ — ${meta.value} (اتركه فارغًا للإبقاء)`;
        }
        if (meta.last4) {
          return `محفوظ — ينتهي بـ ${meta.last4} (اتركه فارغًا للإبقاء)`;
        }
        return 'محفوظ (اتركه فارغًا للإبقاء)';
      }
      return 'أدخل القيمة';
    },
    formatLast4(meta) {
      if (!meta || !meta.set) return '—';
      return meta.last4 ? `••••${meta.last4}` : '••••';
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      setTimeout(() => {
        this.alert.show = false;
      }, 4000);
    },
  },
});
</script>
